import React, { useState, useEffect, useCallback, useContext } from 'react';
import ReactDOM from 'react-dom';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { 
  DocumentIcon,
  ArrowDownTrayIcon,
  ExclamationCircleIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  DocumentArrowUpIcon,
  TrashIcon,
  DocumentTextIcon,
  PhotoIcon,
  DocumentPlusIcon
} from '@heroicons/react/24/outline';
import ConfirmationModal from './ConfirmationModal';
import { ModalShell, ModalActions, ModalSection } from './modals/core';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { GlobalWorkerOptions } from 'pdfjs-dist';

interface DocumentType {
  id: number;
  description: string;
  category_name: string;
  is_optional: boolean;
}

interface DogDocument {
  id: number;
  filename: string;
  description: string;
  category: string;
  upload_date: string;
  doc_type_id?: number;
}

interface MissingDocument {
  doc_type_id: number;
  description: string;
}

interface MyDogDocumentsProps {
  dogId: string;
}

// PDF.js Worker-Setup
const PDFJS_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

const MyDogDocuments: React.FC<MyDogDocumentsProps> = ({ dogId }) => {
  const { config } = useContext(AuthContext);
  const [documents, setDocuments] = useState<DogDocument[]>([]);
  const [missingDocs, setMissingDocs] = useState<MissingDocument[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<DogDocument | null>(null);
  const [docUrlForPreview, setDocUrlForPreview] = useState<string | null>(null);
  const [docErrorForPreview, setDocErrorForPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'warning' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'two' as 'single' | 'two',
    onConfirm: () => {},
  });

  const BASE_URL = process.env.REACT_APP_API_URL || config?.apiUrl || 'http://localhost:4000';

  // PDF.js Worker konfigurieren
  useEffect(() => {
    GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
  }, []);

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadDocumentTypes = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/dogs/dog_document_types');
      console.log('Document types response:', res.data);
      
      if (res.data.success) {
        const rawTypes = res.data.documentTypes || [];
        const validTypes = rawTypes.filter((dt: any) => 
          dt && typeof dt.description === 'string' && dt.description.trim() !== ''
        );
        validTypes.sort((a: DocumentType, b: DocumentType) => {
          if (a.is_optional === b.is_optional) return a.description.localeCompare(b.description);
          return a.is_optional ? 1 : -1;
        });
        console.log('Valid document types:', validTypes);
        setDocumentTypes(validTypes);
      } else {
        setError(res.data.message || 'Fehler beim Laden der Dokumenttypen');
        setDocumentTypes([]);
      }
    } catch (err) {
      console.error('Error loading document types:', err);
      setError('Netzwerkfehler beim Laden der Dokumenttypen');
      setDocumentTypes([]);
    }
  }, []);

  const loadDogDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/api/dogs/get_dog_documents/${dogId}`);
      if (res.data.success) {
        setDocuments(res.data.documents || []);
      } else {
        setError(res.data.message || 'Fehler beim Laden der Dokumente');
        setDocuments([]);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError('Netzwerkfehler beim Laden der Dokumente');
      }
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [dogId]);

  const checkRequiredDocuments = useCallback(async () => {
    try {
      const res = await apiClient.get(`/api/dogs/check_required_documents/${dogId}`);
      setMissingDocs(!res.data.success && res.data.missing_documents ? res.data.missing_documents : []);
    } catch (error: any) {
      if (error.response?.status !== 401) {
        setMissingDocs([]);
      }
    }
  }, [dogId]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await loadDocumentTypes();
      await loadDogDocuments();
      await checkRequiredDocuments();
      setIsLoading(false);
    };
    loadData();
  }, [loadDocumentTypes, loadDogDocuments, checkRequiredDocuments]);

  // Cleanup: Re-enable scrolling when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleDeleteDocument = (docId: number, docName: string) => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Dokument löschen',
      message: `Möchtest du dieses Dokument "${docName}" wirklich endgültig löschen?`,
      variant: 'two',
      onConfirm: async () => {
        try {
          const res = await apiClient.delete(`/api/dogs/delete_dog_document/${dogId}/${docId}`);
          if (res.data.success) {
            await Promise.all([loadDogDocuments(), checkRequiredDocuments()]);
            showNotification('Das Dokument wurde erfolgreich gelöscht', 'success');
          } else {
            setError(res.data.message || 'Fehler beim Löschen des Dokuments');
          }
        } catch (err: any) {
          if (err.response?.status !== 401) {
            setError('Netzwerkfehler beim Löschen des Dokuments');
          }
        }
      },
    });
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const getOrderedGroupedDocuments = useCallback(() => {
    const categoryOrder = documentTypes.map(dt => dt.category_name).filter(name => name);
    const grouped = documents.reduce<Record<string, DogDocument[]>>((acc, doc) => {
      const categoryKey = (doc.category && typeof doc.category === 'string' && doc.category.trim() !== '') 
        ? doc.category 
        : 'Sonstige Dokumente';
      if (!acc[categoryKey]) acc[categoryKey] = [];
      acc[categoryKey].push(doc);
      return acc;
    }, {});

    categoryOrder.forEach(catName => {
      if (!grouped[catName]) grouped[catName] = [];
    });

    const result = categoryOrder.map(catName => ({
      categoryName: catName,
      docs: grouped[catName] || []
    }));

    Object.keys(grouped).forEach(catName => {
      if (!categoryOrder.includes(catName)) {
        result.push({ categoryName: catName, docs: grouped[catName] });
      }
    });

    return result.filter(group => 
      !(group.categoryName === 'Sonstige Dokumente' && 
        group.docs.length === 0 && 
        !categoryOrder.includes('Sonstige Dokumente'))
    );
  }, [documents, documentTypes]);

  const loadDocumentForPreview = async (doc: DogDocument) => {
    setDocUrlForPreview(null);
    setDocErrorForPreview(null);
    
    try {
      const response = await apiClient.get(`/api/dogs/documents/download/${doc.id}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf, image/*'
        }
      });

      const blob = response.data;
      const mimeType = response.headers['content-type'] || 
        (doc.filename.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream');
      const blobUrl = URL.createObjectURL(new Blob([blob], { type: mimeType }));
      setDocUrlForPreview(blobUrl);
    } catch (error: any) {
      if (error.response?.status !== 401) {
        let errorMsg = `HTTP-Fehler ${error.response?.status || 'Unbekannt'}`;
        if (error.response?.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.message) {
          errorMsg = error.message;
        }
        setDocErrorForPreview(`Vorschau konnte nicht geladen werden: ${errorMsg}`);
      }
    }
  };

  const handlePreview = async (doc: DogDocument) => {
    setSelectedDoc(doc);
    await loadDocumentForPreview(doc);
    setShowPreviewModal(true);
    
    // Disable scrolling when modal opens
    document.body.style.overflow = 'hidden';
  };

  const handleClosePreviewModal = () => {
    if (docUrlForPreview && docUrlForPreview.startsWith('blob:')) {
      URL.revokeObjectURL(docUrlForPreview);
    }
    setDocUrlForPreview(null);
    setDocErrorForPreview(null);
    setShowPreviewModal(false);
    setSelectedDoc(null);
    
    // Re-enable scrolling
    document.body.style.overflow = '';
  };

  const getFileIcon = (filename: string) => {
    if (!filename) return <DocumentIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />;
    const extension = filename.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return <DocumentTextIcon className="h-6 w-6 text-error-500 dark:text-error-400" />;
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(extension || '')) {
      return <PhotoIcon className="h-6 w-6 text-info-500 dark:text-info-400" />;
    }
    return <DocumentIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Datum unbekannt';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy', { locale: de });
    } catch {
      return 'Ungültiges Datum';
    }
  };

  const orderedGroupedDocs = getOrderedGroupedDocuments();

  if (isLoading && !documents.length) {
    return (
      <div className="text-center py-6">
        <div className="animate-spin h-6 w-6 border-2 border-primary-600 border-t-transparent rounded-full mx-auto" />
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Lade Dokumente...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Notification */}
      {notification && (
        <div className={`p-3 rounded-lg flex items-center ${
          notification.type === 'success' 
            ? 'bg-success-100 dark:bg-success-900/20 text-success-700 dark:text-success-300'
            : notification.type === 'error'
            ? 'bg-error-100 dark:bg-error-900/20 text-error-700 dark:text-error-300'
            : 'bg-info-100 dark:bg-info-900/20 text-info-700 dark:text-info-300'
        }`}>
          <ExclamationCircleIcon className="h-5 w-5 mr-2" />
          {notification.message}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-error-100 dark:bg-error-900/20 border border-error-300 dark:border-error-600 text-error-700 dark:text-error-200 px-4 py-2.5 rounded-lg text-sm">
          <ExclamationCircleIcon className="inline h-4 w-4 mr-2" />
          {error}
        </div>
      )}

      {/* Missing Documents Warning */}
      {missingDocs.length > 0 && (
        <div className="bg-warning-50 dark:bg-warning-700/30 border border-warning-300 dark:border-warning-600/50 text-warning-700 dark:text-warning-200 px-3 py-2 rounded-lg text-sm flex items-start">
          <ExclamationCircleIcon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-semibold">Fehlende Pflichtdokumente:</span>
            <ul className="list-disc list-inside ml-1 mt-1">
              {missingDocs.map(doc => (
                <li key={doc.doc_type_id}>{doc.description}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="space-y-3">
        {orderedGroupedDocs.length === 0 ? (
          <div className="bg-gray-50 dark:bg-gray-700/60 p-4 rounded-lg text-center">
            <DocumentIcon className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Bisher keine Dokumente für diesen Hund hochgeladen.
            </p>
          </div>
        ) : (
          orderedGroupedDocs.map(({ categoryName, docs }) => (
            <div key={categoryName} className="bg-gray-100 dark:bg-gray-700/30 rounded-xl shadow">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(categoryName)}
                className="w-full flex items-center justify-between p-4 text-left font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-200/70 dark:hover:bg-gray-600/50 rounded-t-xl transition-colors"
              >
                <span className="truncate" title={categoryName || 'Unbekannte Kategorie'}>{categoryName || 'Unbekannte Kategorie'}</span>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center justify-center text-xs font-bold rounded-full w-6 h-6 ${
                    docs.length > 0 
                      ? 'bg-primary-500 dark:bg-primary-600 text-white' 
                      : 'bg-gray-300 dark:bg-gray-500 text-gray-700 dark:text-gray-200'
                  }`}>
                    {docs.length}
                  </span>
                  {expandedCategories[categoryName] ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  )}
                </div>
              </button>

              {/* Document Items */}
              {expandedCategories[categoryName] && (
                <div className="p-3 space-y-2 border-t border-gray-200 dark:border-gray-600/50">
                  {docs.length > 0 ? (
                    docs.map(doc => (
                      <div
                        key={doc.id}
                        className="bg-white dark:bg-gray-800 p-3 rounded-lg flex justify-between items-center hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center gap-3 flex-grow overflow-hidden">
                          {getFileIcon(doc.filename)}
                          <div className="flex-grow overflow-hidden">
                            <p className="font-semibold text-gray-700 dark:text-gray-100 text-sm truncate">
                              {doc.description === 'Keine Angaben' ? doc.filename : doc.description}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Hochgeladen: {formatDate(doc.upload_date)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handlePreview(doc)}
                            className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                            title="Vorschau"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <a
                            href={`${BASE_URL}/api/dogs/documents/download/${doc.id}`}
                            download
                            className="p-2 text-info-600 hover:bg-info-50 dark:hover:bg-info-900/20 rounded-lg transition-colors"
                            title="Herunterladen"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                          </a>
                          <button
                            onClick={() => handleDeleteDocument(doc.id, doc.description === 'Keine Angaben' ? doc.filename : doc.description)}
                            className="p-2 text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors"
                            title="Löschen"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 px-2 py-4 text-center">
                      Keine Dokumente in dieser Kategorie.
                    </p>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Upload Button */}
      <button
        onClick={() => {
          console.log('Upload button clicked. Document types:', documentTypes);
          if (documentTypes.length === 0) {
            showNotification('Keine Dokumenttypen verfügbar. Bitte wenden Sie sich an den Administrator.', 'error');
            return;
          }
          setShowUploadModal(true);
        }}
        className="w-full flex items-center justify-center gap-2 text-sm font-semibold py-2.5 px-4 rounded-lg border-2 border-dashed border-primary-500 dark:border-primary-600 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:border-solid transition-all"
      >
        <DocumentArrowUpIcon className="h-5 w-5" />
        Neues Dokument hochladen
      </button>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadDocumentModal
          dogId={dogId}
          onClose={() => setShowUploadModal(false)}
          onUpload={(successMessage) => {
            loadDogDocuments();
            checkRequiredDocuments();
            if (successMessage) {
              showNotification(successMessage, 'success');
            }
          }}
          documentTypes={documentTypes}
        />
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedDoc && (
        <PreviewModal
          doc={selectedDoc}
          docUrl={docUrlForPreview}
          docError={docErrorForPreview}
          onClose={handleClosePreviewModal}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, visible: false })
          },
          confirm: {
            text: 'Löschen',
            onPress: () => {
              confirmModal.onConfirm();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          }
        }}
      />
    </div>
  );
};

// Upload Document Modal Component
interface UploadDocumentModalProps {
  dogId: string;
  onClose: () => void;
  onUpload: (message?: string) => void;
  documentTypes: DocumentType[];
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({ 
  dogId, 
  onClose, 
  onUpload, 
  documentTypes 
}) => {
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async () => {
    console.log('Upload attempt:', { categoryId, file, description });
    
    if (!categoryId || !file) {
      setError('Bitte Kategorie und Datei auswählen.');
      return;
    }

    setError(null);
    setIsUploading(true);
    
    const formData = new FormData();
    formData.append('document', file);
    formData.append('category_id', categoryId);
    formData.append('description', description.trim() || file.name);

    try {
      const res = await apiClient.post(`/api/dogs/upload_dog_document/${dogId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        const uploadedFileName = description.trim() || file.name;
        onUpload(`Das Dokument "${uploadedFileName}" wurde erfolgreich hochgeladen.`);
        onClose();
      } else {
        setError(res.data.message || 'Fehler beim Hochladen des Dokuments.');
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError('Netzwerkfehler: Dokument konnte nicht hochgeladen werden.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <ModalShell
      open
      onClose={() => { if (!isUploading) onClose(); }}
      variant="form"
      size="md"
      icon={DocumentArrowUpIcon}
      title="Dokument hochladen"
      closeOnBackdrop={false}
      hideCloseButton={isUploading}
      footer={
        <ModalActions
          secondary={{ label: 'Abbrechen', onClick: onClose, disabled: isUploading }}
          primary={{
            label: isUploading ? 'Lädt hoch...' : 'Hochladen',
            onClick: handleUpload,
            disabled: !categoryId || !file || isUploading,
            loading: isUploading,
          }}
        />
      }
    >
      {error && (
        <div className="mb-4 bg-error-100 dark:bg-error-800/50 border border-error-300 dark:border-error-600/70 text-error-700 dark:text-error-200 px-3 py-2 rounded-md text-sm flex items-center gap-2">
          <ExclamationCircleIcon className="h-4 w-4" />
          {error}
        </div>
      )}

      <ModalSection>
        <div className="space-y-4">
          <div>
            <label htmlFor="docCategory" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Kategorie *
            </label>
            <select
              id="docCategory"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
              disabled={isUploading}
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Kategorie auswählen...</option>
              {documentTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.description} {type.is_optional ? '(Optional)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="docDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Beschreibung (optional)
            </label>
            <input
              id="docDescription"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="z.B. Impfpass Seite 3"
              disabled={isUploading}
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="docFile" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Datei auswählen *
            </label>
            <input
              id="docFile"
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
              disabled={isUploading}
              accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx"
              className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 dark:file:bg-primary-700/50 file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-600/50"
            />
            {file && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Ausgewählt: {file.name}
              </p>
            )}
          </div>
        </div>
      </ModalSection>
    </ModalShell>
  );
};

// Preview Modal Component
interface PreviewModalProps {
  doc: DogDocument;
  docUrl: string | null;
  docError: string | null;
  onClose: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ doc, docUrl, docError, onClose }) => {
  const isPdf = doc.filename.toLowerCase().endsWith('.pdf');
  
  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Datum unbekannt';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy', { locale: de });
    } catch {
      return 'Ungültiges Datum';
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-surface-dark rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h5 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 overflow-hidden">
            {isPdf ? (
              <DocumentTextIcon className="h-5 w-5 text-error-500 dark:text-error-400 flex-shrink-0" />
            ) : (
              <PhotoIcon className="h-5 w-5 text-info-500 dark:text-info-400 flex-shrink-0" />
            )}
            <span className="truncate">
              {doc.description === 'Keine Angaben' ? doc.filename : doc.description}
            </span>
          </h5>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-auto p-4 bg-gray-100 dark:bg-gray-800">
          {docUrl ? (
            isPdf ? (
              <div className="w-full h-full bg-gray-200 dark:bg-gray-700">
                <Worker workerUrl={PDFJS_WORKER_URL}>
                  <div 
                    className="w-full h-full overflow-auto"
                    style={{ 
                      height: '60vh', 
                      minHeight: '500px',
                      maxHeight: '75vh',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <Viewer
                      fileUrl={docUrl}
                      defaultScale={SpecialZoomLevel.PageWidth}
                    />
                  </div>
                </Worker>
              </div>
            ) : (
              <div className="flex justify-center items-center w-full h-full">
                <img
                  src={docUrl}
                  alt={`Vorschau von ${doc.filename}`}
                  className="max-h-full max-w-full object-contain rounded-lg shadow-lg"
                />
              </div>
            )
          ) : docError ? (
            <div className="h-full flex flex-col justify-center items-center text-center">
              <ExclamationCircleIcon className="h-12 w-12 text-error-500 mb-3" />
              <p className="font-semibold text-lg text-gray-900 dark:text-white">Fehler beim Laden</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{docError}</p>
            </div>
          ) : (
            <div className="h-full flex flex-col justify-center items-center text-center">
              <div className="animate-spin h-10 w-10 border-4 border-primary-600 border-t-transparent rounded-full mb-3" />
              <p className="text-gray-600 dark:text-gray-400">Dokumentenvorschau wird geladen...</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            Datei: {doc.filename} (Hochgeladen: {formatDate(doc.upload_date)})
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MyDogDocuments;